// @owners { team: patients }
import { ActionSheetContext } from '@alto/design-system';
import { CSATActionSheet, useCanShowCSAT, useFetchOrderSuccess } from '@alto/features';
import { type Prescription as OrderSuccessPrescription } from '@alto/scriptdash/alto/patient_app/order_success/types/v1/prescription';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { closeCart } from '~shared/actions/ui/cart';
import { getAsapShipmentFee } from '~shared/features/shipment-fees/selectors/getShipmentFeesFromShipment';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getShipmentWithItemsByID } from '~shared/features/shipments/selectors/getShipmentsWithItems';
import { getURLParams } from '~shared/helpers/helper';
import { useIsFeatureDisabledForCompany } from '~shared/hooks';
import { getIsCartShowing } from '~shared/selectors/ui/cart';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { LoadingPage } from '~web/features/essentials/components';
import { OrderSuccessWithProducts } from '~web/features/essentials/pages/OrderSuccess/OrderSuccessWithProducts';
import { OrderSuccessWithoutProducts } from '~web/features/essentials/pages/OrderSuccess/OrderSuccessWithoutProducts';

type EssentialsOrderSuccessRouteParams = {
  readonly shipmentID: string;
};

export const OrderSuccess = () => {
  const dispatch = useDispatchShared();
  const { shipmentID } = useParams<EssentialsOrderSuccessRouteParams>();
  const { split, compound } = getURLParams();
  const isSplit = split === 'true';
  const hasCompound = compound === 'true';

  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { canShowCSAT, updateCSATViewedDate } = useCanShowCSAT();
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID: Number(shipmentID) }));
  const orderSuccessFetchParam = shipment?.id ? [shipment?.id] : [];
  const { shipments, isLoading } = useFetchOrderSuccess(orderSuccessFetchParam);
  const orderSuccessShipment = shipments?.[0];
  const orderSuccessPrescriptions = orderSuccessShipment?.prescriptions || [];
  const isAsapDelivery = !shipment ? false : !!getAsapShipmentFee(shipment);
  const { isFeatureDisabled: shouldDisableOTCForCompany, isLoading: isFeatureDisabledLoading } =
    useIsFeatureDisabledForCompany('otc');
  const shouldNotShowEssentials = isAsapDelivery || shouldDisableOTCForCompany;
  const isCartOpen = useSelectorShared((state) => getIsCartShowing(state));

  const timer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const showCSAT = useCallback(() => {
    if (canShowCSAT) {
      updateCSATViewedDate();
      if (!timer.current) {
        timer.current = setTimeout(() => {
          setActiveActionSheet(<CSATActionSheet />);
        }, 2000);
      }
    }
  }, [canShowCSAT, setActiveActionSheet, updateCSATViewedDate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    showCSAT();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCartOpen) {
      dispatch(closeCart());
    }
  }, [dispatch, isCartOpen]);

  const shipmentWithItems = useSelectorShared((state) =>
    getShipmentWithItemsByID(state, {
      id: shipment?.id || -1,
    }),
  );
  const prescriptions = shipmentWithItems?.items?.map((item) => item.prescription) || [];
  const lastRefillPrescriptionIDs = orderSuccessPrescriptions
    .filter((prescription: OrderSuccessPrescription) => prescription.is_renewable)
    .map((prescription: OrderSuccessPrescription) => prescription.id);
  const lastRefillPrescriptions = prescriptions.filter((prescription) =>
    lastRefillPrescriptionIDs.includes(prescription.id),
  );

  if (isLoading || !shipment || isFeatureDisabledLoading) return <LoadingPage />;

  return shouldNotShowEssentials ? (
    <OrderSuccessWithoutProducts
      shipment={shipment}
      isSplitOrder={isSplit}
      lastRefillPrescriptions={lastRefillPrescriptions}
    />
  ) : (
    <OrderSuccessWithProducts
      shipment={shipment}
      isSplitOrder={isSplit}
      lastRefillPrescriptions={lastRefillPrescriptions}
      hasCompound={hasCompound}
    />
  );
};
